import {
  faCircleExclamation,
  faClock,
  faPause,
  faPlay,
  faRotateRight,
  faServer,
  faSpinner,
  faSquareCheck,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/shadcn/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { useToast } from '@shared/hooks/useToast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMigrationActivity,
  useMigrationErrors,
  useMigrationPause,
  useMigrationProgress,
  useMigrationResume,
  useMigrationDelete,
  useMigrationRetry,
  useMigrationRun,
  useMigrationStart,
} from '../../hooks/useMigration';
import type { MigrationRun, MigrationRunStatus, MigrationScope } from '../../adapters/MigrationApiAdapter';
import { MigrationReconciliationPanel } from './MigrationReconciliationPanel';
import { StartMigrationModal } from './StartMigrationModal';

const STATUS_CONFIG: Record<
  MigrationRunStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof faSpinner }
> = {
  pending: { variant: 'secondary', icon: faClock },
  running: { variant: 'default', icon: faSpinner },
  paused: { variant: 'outline', icon: faPause },
  completed: { variant: 'default', icon: faSquareCheck },
  failed: { variant: 'destructive', icon: faCircleExclamation },
};

interface MigrationTabProps {
  teamId: number;
  clientName: string;
}

export const MigrationTab = ({ teamId, clientName }: MigrationTabProps) => {
  const { t, i18n } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const { data: runData, isLoading, error, refetch } = useMigrationRun(teamId);
  const startMutation = useMigrationStart();
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);

  const run = runData?.data ?? null;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locale = i18n.language === 'es' ? es : undefined;
      return format(date, 'dd/MM/yyyy HH:mm', { locale });
    } catch {
      return '-';
    }
  };

  const handleStartMigration = async ({
    externalTeamId,
    stockFile,
    scope,
  }: {
    externalTeamId: string;
    stockFile?: File;
    scope: MigrationScope;
  }) => {
    await startMutation.mutateAsync({
      childTeamId: teamId,
      externalTeamId,
      stockFile,
      scope,
    });
    toast({
      title: t('tabs.migration.toast.started'),
      description: t('tabs.migration.toast.startedDescription'),
      variant: 'default',
    });
    setIsStartModalOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FontAwesomeIcon icon={faCircleExclamation} className="w-12 h-12 mb-4 opacity-50" />
        <p>{t('tabs.migration.error')}</p>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          {t('buttons.retry')}
        </Button>
      </div>
    );
  }

  if (!run) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <FontAwesomeIcon icon={faServer} className="w-12 h-12 mb-4 opacity-50" />
          <p>{t('tabs.migration.empty')}</p>
          <Button variant="outline" onClick={() => setIsStartModalOpen(true)} className="mt-4">
            <FontAwesomeIcon icon={faPlay} className="w-4 h-4 mr-2" />
            {t('tabs.migration.startMigration')}
          </Button>
        </div>
        <StartMigrationModal
          open={isStartModalOpen}
          onOpenChange={setIsStartModalOpen}
          childName={clientName}
          onStart={handleStartMigration}
          isPending={startMutation.isPending}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MigrationRunDetail
        teamId={teamId}
        clientName={clientName}
        run={run}
        formatDate={formatDate}
        onDeleted={() => refetch()}
      />
    </div>
  );
};

interface MigrationRunDetailProps {
  teamId: number;
  clientName: string;
  run: MigrationRun;
  formatDate: (date: string | null) => string;
  onDeleted?: () => void;
}

const MigrationRunDetail = ({ teamId, clientName, run, formatDate, onDeleted }: MigrationRunDetailProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const deleteConfirmRef = useRef<ConfirmModalRef>(null);
  const runId = run.id;

  const deleteMutation = useMigrationDelete(teamId);
  const { data: progressData, isLoading: progressLoading } = useMigrationProgress(runId, { refetchInterval: 5000 });

  const pauseMutation = useMigrationPause(teamId);
  const resumeMutation = useMigrationResume(teamId);
  const retryMutation = useMigrationRetry(runId);
  const { data: errorsData } = useMigrationErrors(runId);
  const { data: activityData } = useMigrationActivity(runId);

  const migrationRun = progressData?.migration_run ?? run;
  const entities = progressData?.entities ?? run.entity_progress ?? [];
  const overallProgress = (() => {
    const total = entities.reduce((sum, e) => sum + (e.total_count ?? 0), 0);
    const completed = entities.reduce((sum, e) => sum + (e.migrated_count ?? 0), 0);
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  })();
  const status = migrationRun?.status;
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const canReconcile = status === 'completed' || status === 'failed';

  const handlePause = async () => {
    try {
      await pauseMutation.mutateAsync(runId);
      toast({
        title: t('tabs.migration.toast.paused'),
        description: t('tabs.migration.toast.pausedDescription'),
        variant: 'default',
      });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast({
        title: t('tabs.migration.toast.error'),
        description: msg || t('tabs.migration.toast.errorGeneric'),
        variant: 'destructive',
      });
    }
  };

  const handleResume = async () => {
    try {
      await resumeMutation.mutateAsync(runId);
      toast({
        title: t('tabs.migration.toast.resumed'),
        description: t('tabs.migration.toast.resumedDescription'),
        variant: 'default',
      });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast({
        title: t('tabs.migration.toast.error'),
        description: msg || t('tabs.migration.toast.errorGeneric'),
        variant: 'destructive',
      });
    }
  };

  const handleRetry = async (progressId: number) => {
    try {
      await retryMutation.mutateAsync(progressId);
      toast({
        title: t('tabs.migration.toast.retryStarted'),
        description: t('tabs.migration.toast.retryStartedDescription'),
        variant: 'default',
      });
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast({
        title: t('tabs.migration.toast.error'),
        description: msg || t('tabs.migration.toast.errorGeneric'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = () => {
    deleteConfirmRef.current?.open(
      async () => {
        try {
          await deleteMutation.mutateAsync(runId);
          toast({
            title: t('tabs.migration.toast.deleted'),
            description: t('tabs.migration.toast.deletedDescription'),
            variant: 'default',
          });
          onDeleted?.();
        } catch (err: unknown) {
          const msg =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
              : null;
          toast({
            title: t('tabs.migration.toast.error'),
            description: msg || t('tabs.migration.toast.errorGeneric'),
            variant: 'destructive',
          });
        }
      },
      undefined,
      t('tabs.migration.deleteModal.title'),
      t('tabs.migration.deleteModal.message'),
      t('tabs.migration.deleteModal.confirm'),
      t('tabs.migration.deleteModal.cancel'),
    );
  };

  const resolvedScope = migrationRun.scope ?? 'total';

  const config = status ? STATUS_CONFIG[status] : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle>{clientName}</CardTitle>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <Badge variant="outline" className="font-normal">
              {t(`tabs.migration.scopeType.${resolvedScope}`)}
            </Badge>
            {config && (
              <Badge variant={config.variant} className="gap-1">
                {status === 'running' && <FontAwesomeIcon icon={config.icon} className="animate-spin" />}
                {status !== 'running' && <FontAwesomeIcon icon={config.icon} />}
                {t(`tabs.migration.status.${status}`)}
              </Badge>
            )}
          </div>
        </div>
        <CardDescription>
          {t('tabs.migration.startedAt')}: {formatDate(migrationRun.started_at)}
          {migrationRun.finished_at && (
            <>
              {' '}
              · {t('tabs.migration.finishedAt')}: {formatDate(migrationRun.finished_at)}
            </>
          )}
          {migrationRun.external_team_id && <> · ID Legacy: {migrationRun.external_team_id}</>}
        </CardDescription>
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {(isRunning || isPaused) && (
            <>
              {isRunning && (
                <Button size="sm" variant="outline" onClick={handlePause} disabled={pauseMutation.isPending}>
                  <FontAwesomeIcon icon={faPause} className="w-4 h-4 mr-2" />
                  {t('tabs.migration.pause')}
                </Button>
              )}
              {isPaused && (
                <Button size="sm" variant="default" onClick={handleResume} disabled={resumeMutation.isPending}>
                  <FontAwesomeIcon icon={faPlay} className="w-4 h-4 mr-2" />
                  {t('tabs.migration.resume')}
                </Button>
              )}
            </>
          )}
          {status !== 'running' && status !== 'paused' && (
            <>
              <p className="text-sm text-muted-foreground w-full sm:w-auto flex-1 basis-full sm:basis-auto">
                {t('tabs.migration.singleRunOnlyHint')}
              </p>
              <Button size="sm" variant="destructive" onClick={handleDeleteClick} disabled={deleteMutation.isPending}>
                <FontAwesomeIcon icon={faTrash} className="w-4 h-4 mr-2" />
                {t('tabs.migration.deleteRun')}
              </Button>
            </>
          )}
        </div>
        <ConfirmModal ref={deleteConfirmRef} />
      </CardHeader>
      <CardContent className="space-y-4">
        {(isRunning || isPaused) && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{t('tabs.migration.overallProgress')}</span>
              <span>{overallProgress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${overallProgress}%` }} />
            </div>
          </div>
        )}

        <Tabs defaultValue="entities" className="w-full">
          <TabsList>
            <TabsTrigger value="entities">{t('tabs.migration.entities')}</TabsTrigger>
            <TabsTrigger value="errors">
              {t('tabs.migration.errors')} ({errorsData?.data?.length ?? 0})
            </TabsTrigger>
            <TabsTrigger value="activity">{t('tabs.migration.activity')}</TabsTrigger>
            {canReconcile && (
              <TabsTrigger value="reconciliation">{t('tabs.migration.reconciliation.title')}</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="entities" className="mt-4">
            {progressLoading && !progressData ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            ) : entities.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">{t('tabs.migration.noEntities')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('tabs.migration.entityType')}</TableHead>
                    <TableHead>{t('tabs.migration.progress')}</TableHead>
                    <TableHead>{t('tabs.migration.statusLabel')}</TableHead>
                    <TableHead>{t('tabs.migration.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entities.map(
                    (entity: {
                      id: number;
                      entity_type: string;
                      migrated_count: number;
                      total_count: number;
                      failed_count: number;
                      status: string;
                    }) => (
                      <TableRow key={entity.id}>
                        <TableCell className="font-medium">{entity.entity_type}</TableCell>
                        <TableCell>
                          {entity.migrated_count} / {entity.total_count}
                          {entity.failed_count > 0 && (
                            <span className="text-destructive ml-2">
                              ({entity.failed_count} {t('tabs.migration.failed')})
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entity.status === 'failed' ? 'destructive' : 'secondary'}>
                            {t(`tabs.migration.entityStatus.${entity.status}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {entity.status === 'failed' && (isRunning || isPaused) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRetry(entity.id)}
                              disabled={retryMutation.isPending}
                            >
                              <FontAwesomeIcon icon={faRotateRight} className="w-4 h-4 mr-1" />
                              {t('tabs.migration.retry')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="errors" className="mt-4">
            {!errorsData?.data?.length ? (
              <p className="text-sm text-muted-foreground py-4">{t('tabs.migration.noErrors')}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('tabs.migration.entityType')}</TableHead>
                    <TableHead>{t('tabs.migration.externalId')}</TableHead>
                    <TableHead>{t('tabs.migration.errorMessage')}</TableHead>
                    <TableHead>{t('tabs.migration.createdAt')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errorsData.data.map((err) => (
                    <TableRow key={err.id}>
                      <TableCell>{err.entity_type}</TableCell>
                      <TableCell>{err.external_id}</TableCell>
                      <TableCell className="max-w-xs truncate" title={err.error_message}>
                        {err.error_message}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(err.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            {!activityData?.data?.length ? (
              <p className="text-sm text-muted-foreground py-4">{t('tabs.migration.noActivity')}</p>
            ) : (
              <div className="space-y-4">
                {activityData.data.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 p-3 rounded-lg border border-border bg-background"
                  >
                    <FontAwesomeIcon
                      icon={
                        item.action === 'started'
                          ? faPlay
                          : item.action === 'paused'
                            ? faPause
                            : item.action === 'resumed'
                              ? faPlay
                              : item.action === 'retry_failed'
                                ? faRotateRight
                                : faSquareCheck
                      }
                      className="w-4 h-4 mt-0.5 text-muted-foreground"
                    />
                    <div>
                      <p className="text-sm font-medium">
                        {item.user?.name} - {t(`tabs.migration.activityActions.${item.action}`)}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {canReconcile && (
            <TabsContent value="reconciliation" className="mt-4">
              <MigrationReconciliationPanel runId={runId} formatDate={formatDate} />
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};
